import PropTypes from 'prop-types'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import isEmpty from 'lodash/isEmpty'

import * as sessionUserSelector from 'selectors/sessionUser'
import { userTimeZoneFormat } from 'utils/time'
import colors from 'components/utils/colors'

import Table from 'components/Shared/TableNew/Index'
import TableHead from 'components/Shared/TableNew/TableHead'
import TableBody from 'components/Shared/TableNew/TableBody'
import TableRow from 'components/Shared/TableNew/TableRow'
import TableCell from 'components/Shared/TableNew/TableCell'
import TableCellWithActions from 'components/Shared/TableNew/TableCellWithActions'
import TableRowNoData from 'components/Shared/TableNew/TableRowNoData'
import TableCellAction from 'components/Shared/TableNew/TableCellAction'
import Tooltip from 'components/Shared/Tooltip/Index'
import Badge from 'components/Shared/Badge/Index'
import MixedFiltersField from 'components/Shared/MixedFiltersField/Index'
import { stringifyQuery } from 'utils/strings'

import { RouteTicketingSystemsTickets } from 'routes'

const TicketsViewsSubscriptionsTable = ({
  subscriptions, removeTicketsViewsSubscription, ticketingSystemHasOnlyOneBoard,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const userTimeZone = useSelector(sessionUserSelector.timezone)

  const unsubscribe = (subscription) => {
    removeTicketsViewsSubscription({
      ticketingSystemDisplayId: subscription.ticketingSystemDisplayId,
      storedParams: subscription.params,
    })
  }

  const buildViewName = (params) => {
    if (
      isEmpty(params) ||
      (
        !params.status?.length &&
        !params.filter?.length &&
        !params.search?.length &&
        ticketingSystemHasOnlyOneBoard
      )
    ) {
      return <Badge>{t('subscriptionsPage.subscribeToAllTickets')}</Badge>
    }

    const providerTicketBoardParams = !isEmpty(params.providerTicketBoard) && !ticketingSystemHasOnlyOneBoard
      ? {
          title: t('subscriptionsPage.providerTicketBoardText'),
          name: params.providerTicketBoard.name,
          color: params.providerTicketBoard.color,
        }
      : {}

    const statusList = params.status?.length
      ? [{
          title: t('ticketProviderTicketsPage.filtersStatusSectionTitle'),
          items: params.status.map(status => ({
            value: status.name,
            bgColor: status.color,
          })),
        }]
      : []

    const filterList = params.filter?.map(filter => ({
      value: filter.name,
      color: filter.global
        ? (filter.published ? colors.globalGray : colors.infoGray)
        : colors.pausedGray,
    })) || []

    return (
      <MixedFiltersField
        providerTicketBoardParams={providerTicketBoardParams}
        filterList={filterList}
        multipleFilterList={statusList}
        searchValue={{ value: params.search, title: t('subscriptionsPage.SearchText') }}
        maxDisplayItems={3}
        tooltipText={t('ticketProviderTicketsPage.filterPanelFiltersTooltip')}
        tooltipId='tooltip-filter-panel'
        title={t('subscriptionsPage.FiltersText')}
        hideIcon
      />
    )
  }

  const openTicketsViews = (subscription) => {
    const params = {
      search: subscription.params?.search,
      status_filters: subscription.params?.statusFilters,
      ticket_filters: subscription.params?.userFilters,
      provider_ticket_board_id: subscription.params?.providerTicketBoardId,
    }

    navigate({
      pathname: RouteTicketingSystemsTickets({ ticketingSystemDisplayId: subscription.ticketingSystemDisplayId }),
      search: stringifyQuery(params),
    })
  }

  return (
    <>
      <Table width='100%'>
        <TableHead withTopBorder>
          <TableRow>
            <TableCell>{t('subscriptionsPage.subscriptionName')}</TableCell>
            <TableCell>{t('subscriptionsPage.subscriptionCreatedAt')}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>

        <TableBody>
          {subscriptions.map((subscription, key) => {
            return (
              <TableRow
                key={subscription.id}
              >
                <TableCell>
                  {buildViewName(subscription.nameData)}
                </TableCell>

                <TableCell>
                  {userTimeZoneFormat(userTimeZone, subscription.createdAt)}
                </TableCell>

                <TableCellWithActions>
                  <TableCellAction
                    iconName='icon-go-to-16'
                    onClick={
                      () => openTicketsViews(subscription)
                    }
                    tooltip={t('subscriptionsPage.subscriptionGoToViews')}
                    tooltipId={'col-tooltip' + key}
                  />

                  <TableCellAction
                    iconName='icon-remove-16'
                    onClick={
                      () => unsubscribe(subscription)
                    }
                    tooltip={t('subscriptionsPage.unsubscribeTooltip')}
                    tooltipId={'col-tooltip' + key}
                  />
                  <Tooltip id={'col-tooltip' + key} />
                </TableCellWithActions>
              </TableRow>
            )
          })}
          <TableRowNoData data={subscriptions} />
        </TableBody>
      </Table>
    </>
  )
}

TicketsViewsSubscriptionsTable.propTypes = {
  subscriptions: PropTypes.array.isRequired,
  removeTicketsViewsSubscription: PropTypes.func.isRequired,
  ticketingSystemHasOnlyOneBoard: PropTypes.bool,
}

export default TicketsViewsSubscriptionsTable
